import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  orderBy, 
  addDoc,
  updateDoc,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  FaBriefcase, 
  FaPlus, 
  FaSearch, 
  FaMapMarkerAlt, 
  FaClock,
  FaUserTie,
  FaRegCalendarAlt,
  FaChevronLeft,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import { FiExternalLink } from 'react-icons/fi';
import { FaEnvelope, FaPhone, FaTimes } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Spinner from '../components/Spinner';
import FindProfessionalsTab from '../components/FindProfessionalsTab';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Modal from 'react-modal';
import { ROLES } from '../components/roles';

// Custom styles for the modal
const customModalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    border: 'none',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '0',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  },
};

const OrganizationDashboard = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;
  
  // State management
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('jobs'); // 'jobs' or 'applications'
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isPostingJob, setIsPostingJob] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  
  // Handle viewing a candidate's profile
  const handleViewProfile = (candidate) => {
    setSelectedCandidate(candidate);
    setIsProfileModalOpen(true);
  };
  
  // Close profile modal
  const closeProfileModal = () => {
    setIsProfileModalOpen(false);
    setSelectedCandidate(null);
  };
  
  // Set up modal app element on component mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      Modal.setAppElement('#root');
    }
  }, []);
  
  // Form state for job posting
  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    role: '',
    type: 'Full-time',
    location: {
      area: '',
      city: ''
    },
    workLocation: ['Office'],
    skills: [],
    currentSkill: ''
  });

  // Fetch jobs posted by the current organization
  const fetchJobs = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const jobsRef = collection(db, 'jobPosts');
      const q = query(
        jobsRef,
        where('posterId', '==', user.uid),
        orderBy('postedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const jobsData = [];
      
      querySnapshot.forEach((doc) => {
        jobsData.push({
          id: doc.id,
          ...doc.data(),
          postedAt: doc.data().postedAt?.toDate()
        });
      });
      
      setJobs(jobsData);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch applications for a specific job with professional details
  const fetchApplications = useCallback(async (jobId) => {
    if (!jobId) return;
    
    setLoading(true);
    try {
      // First, get all applications for this job
      const applicationsRef = collection(db, 'applications');
      const applicationsQuery = query(
        applicationsRef,
        where('jobId', '==', jobId),
        orderBy('appliedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(applicationsQuery);
      
      // Process applications and fetch professional details
      const apps = [];
      for (const applicationDoc of querySnapshot.docs) {
        try {
          const appData = applicationDoc.data();
          
          // Basic application info
          const applicationInfo = {
            id: applicationDoc.id,
            applicantName: appData.applicantName || 'Anonymous',
            appliedAt: appData.appliedAt?.toDate?.() || new Date(),
            applicantId: appData.applicantId || null,
            coverLetter: appData.coverLetter || '',
            status: appData.status || 'pending',
            // Initialize professional details with defaults
            candidate: {
              displayName: appData.applicantName || 'Anonymous',
              email: appData.applicantEmail || 'No email provided',
              phoneNumber: appData.phoneNumber || '',
              expertise: 'Loading...',
              city: 'Loading...'
            }
          };
          
          // If we have an applicantId, fetch their professional details
          if (appData.applicantId) {
            try {
              const professionalDoc = await getDoc(doc(db, 'professionals', appData.applicantId));
              if (professionalDoc.exists()) {
                const professionalData = professionalDoc.data();
                applicationInfo.candidate = {
                  ...applicationInfo.candidate,
                  // Override with professional details
                  expertise: professionalData.expertise || 'Not specified',
                  city: professionalData.city || 'Not specified',
                  // Include additional professional info if needed
                  ...(professionalData.experience?.length > 0 && {
                    experience: professionalData.experience[0]
                  }),
                  ...(professionalData.education?.length > 0 && {
                    education: professionalData.education[0]
                  })
                };
              } else {
                // If no professional doc found, update with not available
                applicationInfo.candidate.expertise = 'Not available';
                applicationInfo.candidate.city = 'Not available';
              }
            } catch (error) {
              console.error('Error fetching professional details:', error);
              applicationInfo.candidate.expertise = 'Error loading';
              applicationInfo.candidate.city = 'Error loading';
            }
          } else {
            // If no applicantId, mark as not available
            applicationInfo.candidate.expertise = 'Not available';
            applicationInfo.candidate.city = 'Not available';
          }
          
          // Add the application with all the processed info
          apps.push({
            ...applicationInfo,
            // Make sure we have all the original application data
            ...appData,
            // Ensure these are properly set from our processed info
            appliedAt: applicationInfo.appliedAt,
            candidate: applicationInfo.candidate
          });
          
        } catch (error) {
          console.error('Error processing application:', error);
          // Continue with next application even if one fails
          continue;
        }
      }
      
      setApplications(apps);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle job selection
  const handleJobSelect = (job) => {
    setSelectedJob(job);
    setActiveView('applications');
    fetchApplications(job.id);
  };

  // Handle job form input changes
  const handleJobFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setJobForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setJobForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle skills input - removed in favor of inline handler for better UX

  // Handle job form submission
  const handleJobSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('You must be logged in to post a job');
      return;
    }
    
    setLoading(true);
    try {
      const jobData = {
        ...jobForm,
        posterId: user.uid,
        posterEmail: user.email,
        posterType: 'organization',
        status: 'open',
        postedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        searchTerms: [
          `${jobForm.role} required for ${jobForm.location.city} location`,
          jobForm.role,
          jobForm.type.toLowerCase(),
          ...jobForm.skills.map(skill => skill.toLowerCase())
        ]
      };
      
      await addDoc(collection(db, 'jobPosts'), jobData);
      
      toast.success('Job posted successfully!');
      setJobForm({
        title: '',
        description: '',
        role: '',
        type: 'Full-time',
        location: { area: '', city: '' },
        workLocation: ['Office'],
        skills: []
      });
      setIsPostingJob(false);
      fetchJobs();
    } catch (error) {
      console.error('Error posting job:', error);
      toast.error('Failed to post job');
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (user) {
      fetchJobs();
    } else {
      navigate('/login');
    }
  }, [user, navigate, fetchJobs]);

  // Filter jobs based on search term
  const filteredJobs = jobs.filter(job => 
    job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.location?.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.skills?.some(skill => 
      skill.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Render job posting form
  if (isPostingJob) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-6">
          <div className="max-w-3xl mx-auto">
            <div className="mb-10">
              <button 
                onClick={() => setIsPostingJob(false)}
                className="flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
              >
                <FaChevronLeft className="mr-2" /> Back to Jobs
              </button>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h1 className="text-2xl font-bold text-gray-800 mb-6">Post a New Job</h1>
              
              <form onSubmit={handleJobSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={jobForm.title}
                    onChange={handleJobFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="E.g., Speech Therapist"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role *
                  </label>
                  <select
                    name="role"
                    value={jobForm.role}
                    onChange={handleJobFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select a role</option>
                    {ROLES.map((role) => (
                      <option key={role.key} value={role.key}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Job Type *
                    </label>
                    <select
                      name="type"
                      value={jobForm.type}
                      onChange={handleJobFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Freelance">Freelance</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Work Location *
                    </label>
                    <select
                      name="workLocation"
                      value={jobForm.workLocation[0]}
                      onChange={(e) => setJobForm(prev => ({
                        ...prev,
                        workLocation: [e.target.value]
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="Office">Office</option>
                      <option value="Remote">Remote</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Area *
                    </label>
                    <input
                      type="text"
                      name="location.area"
                      value={jobForm.location.area}
                      onChange={handleJobFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="E.g., Rohini"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City *
                    </label>
                    <input
                      type="text"
                      name="location.city"
                      value={jobForm.location.city}
                      onChange={handleJobFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="E.g., New Delhi"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Description *
                  </label>
                  <textarea
                    name="description"
                    value={jobForm.description}
                    onChange={handleJobFormChange}
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter detailed job description, requirements, and responsibilities"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Required Skills (comma-separated)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={jobForm.currentSkill || ''}
                      onChange={(e) => {
                        // Track the current skill being typed
                        setJobForm(prev => ({
                          ...prev,
                          currentSkill: e.target.value
                        }));
                      }}
                      onKeyDown={(e) => {
                        // Add skill on Enter, comma, or space
                        if ((e.key === 'Enter' || e.key === ',' || e.key === ' ') && jobForm.currentSkill.trim()) {
                          e.preventDefault();
                          const newSkill = jobForm.currentSkill.trim().replace(/[,\s]+$/, '');
                          if (newSkill) {
                            setJobForm(prev => ({
                              ...prev,
                              skills: [...prev.skills, newSkill],
                              currentSkill: ''
                            }));
                          }
                        }
                        // Remove last skill on backspace when input is empty
                        else if (e.key === 'Backspace' && !jobForm.currentSkill && jobForm.skills.length > 0) {
                          e.preventDefault();
                          setJobForm(prev => ({
                            ...prev,
                            skills: prev.skills.slice(0, -1)
                          }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={jobForm.skills.length === 0 ? "Type a skill and press Enter or add a comma" : ""}
                    />
                    {jobForm.skills.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {jobForm.skills.map((skill, index) => (
                          <div 
                            key={index} 
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 transition-colors cursor-pointer group"
                            onClick={() => {
                              // Remove the clicked skill
                              setJobForm(prev => ({
                                ...prev,
                                skills: prev.skills.filter((_, i) => i !== index)
                              }));
                            }}
                          >
                            {skill}
                            <span className="ml-1.5 text-blue-600 group-hover:text-blue-900">
                              &times;
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsPostingJob(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {loading ? 'Posting...' : 'Post Job'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Mask contact information
  const maskContactInfo = (info) => {
    if (!info) return 'Not provided';
    const visibleChars = Math.min(3, Math.floor(info.length * 0.3));
    return info.substring(0, visibleChars) + '*'.repeat(info.length - visibleChars);
  };

  // Render applications view
  if (activeView === 'applications' && selectedJob) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <button 
              onClick={() => setActiveView('jobs')}
              className="flex items-center text-blue-600 hover:text-blue-800 mb-6"
            >
              <FaChevronLeft className="mr-1" /> Back to Jobs
            </button>
            
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h1 className="text-2xl font-bold text-gray-800 mb-2">{selectedJob.title}</h1>
              <div className="flex flex-wrap items-center text-sm text-gray-600 mb-4">
                <span className="flex items-center mr-4 mb-2">
                  <FaBriefcase className="mr-1" /> {selectedJob.role}
                </span>
                <span className="flex items-center mr-4 mb-2">
                  <FaMapMarkerAlt className="mr-1" /> {selectedJob.location.area}, {selectedJob.location.city}
                </span>
                <span className="flex items-center mr-4 mb-2">
                  <FaClock className="mr-1" /> {selectedJob.type}
                </span>
                <span className="flex items-center mb-2">
                  <FaRegCalendarAlt className="mr-1" /> 
                  Posted on {selectedJob.postedAt?.toLocaleDateString()}
                </span>
              </div>
              <p className="text-gray-700 mb-4">{selectedJob.description}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedJob.skills?.map((skill, index) => (
                  <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Applications <span className="text-gray-500 font-normal">({applications.length})</span>
                  </h2>
                  <div className="flex items-center space-x-2">
                    <button className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              
              {loading ? (
                <div className="flex justify-center py-12">
                  <Spinner size="md" />
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-12 px-6">
                  <div className="mx-auto h-16 w-16 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                    <FaUserTie className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-base font-medium text-gray-900 mb-1">No applications yet</h3>
                  <p className="text-sm text-gray-500 max-w-md mx-auto">
                    When candidates apply for this position, their applications will appear here.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {applications.map((application) => (
                    <div key={application.id} className="p-5 hover:bg-gray-50 transition-colors duration-150">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                              {application.candidate?.displayName ? application.candidate.displayName.charAt(0).toUpperCase() : 'A'}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between">
                                <h3 className="text-sm font-medium text-gray-900 truncate">
                                  {application.candidate?.displayName || 'Anonymous'}
                                </h3>
                                <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                                  {application.appliedAt?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              </div>
                              <div className="flex items-center space-x-3 mt-1">
                                {application.candidate?.expertise && (
                                  <span className="text-sm text-gray-600">
                                    {application.candidate.expertise}
                                  </span>
                                )}
                                {application.candidate?.city && application.candidate.city !== 'Loading...' && (
                                  <span className="flex items-center text-sm text-gray-500">
                                    <FaMapMarkerAlt className="mr-1 text-xs" />
                                    {application.candidate.city}
                                  </span>
                                )}
                              </div>
                              {application.coverLetter && (
                                <button 
                                  onClick={() => {
                                    setSelectedCandidate({
                                      ...application.candidate,
                                      coverLetter: application.coverLetter
                                    });
                                    setIsProfileModalOpen(true);
                                  }}
                                  className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  View cover letter
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          <a
                            href={`https://wa.me/91${application.candidate?.phoneNumber?.replace(/\D/g, '') || '8978733122'}?text=Hi%20${encodeURIComponent(application.candidate?.displayName?.split(' ')[0] || 'there')},%20I%20saw%20your%20application%20for%20${encodeURIComponent(selectedJob.title)}%20at%20ASDflo.`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
                          >
                            <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                              <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                            </svg>
                            Contact
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Render main jobs view
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Candidate Profile Modal */}
      {isProfileModalOpen && (
        <Modal
          isOpen={isProfileModalOpen}
          onRequestClose={closeProfileModal}
          style={customModalStyles}
          contentLabel="Candidate Profile"
        >
        {selectedCandidate && (
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedCandidate.displayName || 'Candidate Profile'}
                </h2>
                <p className="text-sm text-gray-500">Professional Profile</p>
              </div>
              <button
                onClick={closeProfileModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Name</p>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedCandidate.displayName || 'Not provided'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Email</p>
                      <p className="mt-1 text-sm text-gray-900">
                        {maskContactInfo(selectedCandidate.email) || 'Not provided'}
                      </p>
                    </div>
                    {selectedCandidate.phoneNumber && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Phone</p>
                        <p className="mt-1 text-sm text-gray-900">
                          {maskContactInfo(selectedCandidate.phoneNumber)}
                        </p>
                      </div>
                    )}
                    {selectedCandidate.location && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Location</p>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedCandidate.location}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Professional Summary */}
              {(selectedCandidate.bio || selectedCandidate.summary) && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Professional Summary</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700 whitespace-pre-line">
                      {selectedCandidate.bio || selectedCandidate.summary || 'Not provided'}
                    </p>
                  </div>
                </div>
              )}

              {/* Experience */}
              {selectedCandidate.experience?.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Experience</h3>
                  <div className="space-y-4">
                    {selectedCandidate.experience.map((exp, idx) => (
                      <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900">{exp.position}</h4>
                        <p className="text-sm text-gray-600">{exp.company}</p>
                        <p className="text-xs text-gray-500">
                          {exp.startDate} - {exp.current ? 'Present' : exp.endDate || 'Present'}
                        </p>
                        {exp.description && (
                          <p className="mt-2 text-sm text-gray-700">{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Education */}
              {selectedCandidate.education?.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Education</h3>
                  <div className="space-y-4">
                    {selectedCandidate.education.map((edu, idx) => (
                      <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900">{edu.degree}</h4>
                        <p className="text-sm text-gray-600">{edu.school}</p>
                        <p className="text-xs text-gray-500">
                          {edu.startYear} - {edu.endYear || 'Present'}
                        </p>
                        {edu.fieldOfStudy && (
                          <p className="mt-1 text-sm text-gray-700">{edu.fieldOfStudy}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Skills */}
              {selectedCandidate.skills?.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedCandidate.skills.map((skill, idx) => (
                      <span 
                        key={idx}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Action */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeProfileModal}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Close
                  </button>
                  <a
                    href={`https://wa.me/91${selectedCandidate.phoneNumber?.replace(/\D/g, '') || '8978733122'}?text=Hi%20${encodeURIComponent((selectedCandidate.displayName || 'there').split(' ')[0])},%20I%20saw%20your%20profile%20on%20ASDflo%20and%20would%20like%20to%20discuss%20an%20opportunity.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <svg className="-ml-1 mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 2.003a1 1 0 00-.707 1.71l.847.847a1.5 1.5 0 01.41 1.317l-1.16 4.174a1 1 0 00.293.98l7 7a1 1 0 001.414 0l4.5-4.5a1 1 0 000-1.414l-7-7a1 1 0 00-.98-.293l-4.174 1.16a1.5 1.5 0 01-1.316-.41l-.847-.848a1 1 0 00-.71-.293z" />
                      <path d="M16 3a1 1 0 100-2h-4a1 1 0 100 2h4z" />
                      <path d="M12 7a1 1 0 100-2H8a1 1 0 100 2h4z" />
                    </svg>
                    Contact via WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
        </Modal>
      )}
      <Header />
      <main className="min-h-screen bg-gradient-to-br from-gray-25 to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Welcome Section */}
          <div className="mb-12 bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center">
              <div className="mr-4 flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-r from-blue-500 to-blue-600">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Welcome back, {user?.displayName?.split(' ')[0] || 'there'}! 👋</h1>
                <p className="text-gray-600">Here's an overview of your job postings and applications</p>
              </div>
            </div>
          </div>

          {/* Header Section */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-10 gap-6">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 font-sans">My Job Postings</h2>
              <p className="mt-1 text-gray-600">Manage your active job listings and applications</p>
            </div>
            <div className="w-full sm:w-auto mt-4 sm:mt-0">
              <button
                onClick={() => setIsPostingJob(true)}
                className="w-full sm:w-auto flex items-center justify-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:-translate-y-0.5"
              >
                <FaPlus className="mr-2" />
                Post a New Job
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <div className="relative max-w-xl">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-700"
                placeholder="Search jobs by title, role, or skills..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            {loading ? (
              <div className="flex justify-center py-16">
                <Spinner size="lg" />
              </div>
            ) : filteredJobs.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-blue-50 mb-4">
                  <FaBriefcase className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">No jobs posted yet</h3>
                <p className="mt-2 text-gray-500 max-w-md mx-auto">
                  Get started by posting your first job opening to find the perfect candidates.
                </p>
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={() => setIsPostingJob(true)}
                    className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:-translate-y-0.5"
                  >
                    <FaPlus className="mr-2" />
                    Post Your First Job
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                  {filteredJobs.map((job) => (
                    <div
                      key={job.id}
                      onClick={() => handleJobSelect(job)}
                      className="group h-full flex flex-col bg-white overflow-hidden rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-100 hover:border-blue-100 hover:-translate-y-1"
                    >
                      <div className="p-6 flex-1 flex flex-col">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                              {job.title}
                            </h3>
                            <p className="mt-1 text-sm font-medium text-blue-600">{job.role}</p>
                          </div>
                          <div className="mt-3 sm:mt-0">
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                              (job.status === 'Open' || !job.status) 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {job.status || 'Open'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-gray-600">
                          <div className="flex items-center">
                            <FaMapMarkerAlt className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            <span>{job.location.area}, {job.location.city}</span>
                          </div>
                          <div className="h-1 w-1 rounded-full bg-gray-300"></div>
                          <div className="flex items-center">
                            <FaClock className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            <span>{job.type}</span>
                          </div>
                        </div>
                        
                        <div className="mt-4 flex-1">
                          <p className="text-sm text-gray-600 line-clamp-3">
                            {job.description}
                          </p>
                        </div>
                        
                        {job.skills?.length > 0 && (
                          <div className="mt-4 flex flex-wrap gap-2">
                            {job.skills.slice(0, 5).map((skill, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100"
                              >
                                {skill}
                              </span>
                            ))}
                            {job.skills.length > 5 && (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
                                +{job.skills.length - 5} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-t border-gray-100">
                        <div className="text-sm text-gray-500">
                          <span className="font-medium">Posted:</span> {job.postedAt?.toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </div>
                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleJobSelect(job);
                            }}
                            className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500 focus:outline-none group-hover:underline"
                          >
                            View Applications
                            <svg className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" 
                                 fill="currentColor" 
                                 viewBox="0 0 20 20"
                                 aria-hidden="true">
                              <path
                                fillRule="evenodd"
                                d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      {/* --- Find Professionals Tab/Section --- */}
      <FindProfessionalsTab 
        limitResults={12} 
        showHeader={true} 
        headerText="Find Professionals" 
        showRoleFilter={false}
        showSearchControls={true}
      />
      </main>
      <Footer />
    </div>
  );
};

export default OrganizationDashboard;
